import {
  AnalyticsEvents,
  type ByoPostCreatedProperties,
  type ByoUploadOpenedProperties,
  type PostScheduledProperties,
} from '@mondaygpt/analytics-events'
import { BYO_MAX_CAROUSEL_ITEMS, MAX_INTENT_INGREDIENTS, type LivePlatform } from '@mondaygpt/types'
import { Button } from 'components'
import { format } from 'date-fns'
import { cn } from 'lib/utils'
import { Loader2, Plus, Upload, Video, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { ulid } from 'ulid'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { useCreditGate } from 'modules/billing'
import { apiCall } from 'modules/core/services/api/api'
import { CreationIntentService } from 'openapi/services/CreationIntentService'
import { CreativeService } from 'openapi/services/CreativeService'
import { MediaService } from 'openapi/services/MediaService'
import {
  buildScheduledInstant,
  getBrowserTimezone,
  isTimeOptionInPast,
  tomorrowInTz,
} from '../../common/utils/business-timezone'
import { getScheduleTimeForDate } from '../../common/utils/schedule-time-options'
import { usePlatformConnections } from '../../creative/hooks/usePlatformConnections'
import { HeroModeToggle, type HeroMode } from '../../creative/components/HeroModeToggle'
import { PostModal } from '../../creative/components/PostModal'
import { ShapeSlider, type IntentShape } from '../../creative/components/ShapeSlider'
import { useBrandKitStore } from '../../creative/store/brand-kit-store'
import { useIdeaStore, type PostIdea } from '../../creative/store/idea-store'
import {
  BYO_IMAGE_TYPES,
  BYO_VIDEO_TYPES,
  INGREDIENT_MAX_IMAGE_BYTES,
  INGREDIENT_MAX_VIDEO_BYTES,
  byoAcceptAttrFor,
  byoFilesError,
  byoInviteFor,
  byoMediaKind,
} from '../../creative/utils/byo-media'
import { extractApiError } from '../../creative/utils/extract-api-error'
import { markCalendarGenerationTriggered } from '../analytics/calendar-generation-tracking'
import { useCalendarStore } from '../store/calendar-store'
import { getViewDateRange } from './CalendarPopoverCard'

interface CreatePostButtonProps {
  date: string
  variant?: 'icon' | 'full' | 'hint'
  alwaysVisible?: boolean
  onCreated?: () => void
}

/**
 * The calendar's day-tap creation entry. Tapping a day opens a mini creation
 * moment (CreateMomentPopover): the Home hero's card in miniature, with the
 * same two rooms declared up front (Create with Taka / Post your own).
 * Platform is the composer's job (One Post / master+variants), so there is
 * no platform menu here anymore.
 */
export function CreatePostButton({
  date,
  variant = 'icon',
  alwaysVisible = false,
  onCreated,
}: CreatePostButtonProps) {
  const fetchEntries = useCalendarStore(s => s.fetchEntries)
  const loadIdea = useIdeaStore(s => s.loadIdea)
  const brandTimezone = useBrandKitStore(s => s.brandKit.timezone)
  const { track } = useAnalytics()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // ── Bring-your-own-content: own room's files → post exists → PostModal ───
  // The popover's Post-your-own room collects files + caption; this handler
  // is its CTA: upload, create scheduled onto the tapped day, then the
  // standard PostModal opens on the real preview for platforms + time (fold
  // decision, DESIGN.md). Uploads bypass the credit gate — the media
  // publishes untouched, 0 credits.
  // Connect-gate parity with the composer (monkey QUESTION, Matan: option a):
  // scheduling from a day slot used to bypass the connect gate entirely — the
  // create below pre-scheduled the post before PostModal's gate could run.
  // With no connected channel, create a DRAFT instead; the PostModal that
  // opens next owns platforms + time and runs the standard OAuth gate on its
  // schedule action.
  const { connections } = usePlatformConnections()
  const hasPublishableConnection = Object.values(connections ?? {}).some(
    c => (c as { connected?: boolean } | undefined)?.connected
  )
  const [byoPhase, setByoPhase] = useState<'idle' | 'uploading' | 'creating'>('idle')
  const [byoError, setByoError] = useState<string | null>(null)
  const [byoIdea, setByoIdea] = useState<PostIdea | null>(null)

  const handleScheduleOwn = useCallback(
    async (files: File[], caption: string) => {
      if (files.length === 0) return
      // Belt: the popover validates at add time, but the mix can only be
      // judged over the full set.
      const error = byoFilesError(files)
      if (error) {
        setByoError(error)
        return
      }
      setByoError(null)
      setByoPhase('uploading')
      try {
        const attachments = await Promise.all(
          files.map(file =>
            apiCall(MediaService.mediaControllerUploadFileV1, null, { file, id: ulid() })
          )
        )
        setByoPhase('creating')
        // The tapped day's default slot; when every slot today already passed
        // (late-night create), roll the DATE forward and keep the wall-clock
        // time — the server 400s past instants.
        const tz = brandTimezone ?? getBrowserTimezone()
        const preferredTime = getScheduleTimeForDate(date, tz)
        const scheduleDate = isTimeOptionInPast(date, preferredTime, tz) ? tomorrowInTz(tz) : date
        const scheduledAt = buildScheduledInstant(scheduleDate, preferredTime, tz)

        const res = await apiCall(CreativeService.creativeControllerCreateFromUploadV1, null, {
          attachmentIds: attachments.map(a => a.id),
          caption: caption || undefined,
          ...(hasPublishableConnection ? { scheduledAt } : {}),
          // No platforms: the server picks the default primary; the user fans
          // out in the PostModal that opens next.
        })

        const platforms = res.creatives.map(c => c.type) as LivePlatform[]
        const mediaKind = byoMediaKind(files.map(f => f.type))
        track(AnalyticsEvents.Posts.BYO_POST_CREATED, {
          source: 'calendar',
          media_kind: mediaKind,
          platforms,
          platform_count: platforms.length,
          scheduled: hasPublishableConnection,
          has_caption: caption.length > 0,
        } satisfies ByoPostCreatedProperties)
        const leadTimeMinutes = Math.max(
          0,
          Math.round((new Date(scheduledAt).getTime() - Date.now()) / 60_000)
        )
        if (hasPublishableConnection)
          track(AnalyticsEvents.Posts.POST_SCHEDULED, {
            source: 'byo_upload',
            creative_id: res.creatives[0].id,
            platforms,
            platform_count: platforms.length,
            scheduled_at: scheduledAt,
            lead_time_minutes: leadTimeMinutes,
            is_immediate: false,
            entry_source: 'byo_upload',
            via_composer: false,
          } satisfies PostScheduledProperties)

        // Refetch the visible range — the calendar store never hears about
        // server-direct creates, and most mount sites pass no onCreated.
        const { from, to } = getViewDateRange()
        void fetchEntries(from, to)
        onCreated?.()
        setOpen(false)

        const idea = await loadIdea(res.project.id)
        setByoPhase('idle')
        if (idea) setByoIdea(idea)
      } catch (err) {
        console.error('BYO create-from-upload failed:', err)
        setByoError(extractApiError(err, "We couldn't create this post. Please try again."))
        setByoPhase('idle')
      }
    },
    [date, brandTimezone, hasPublishableConnection, fetchEntries, loadIdea, onCreated, track]
  )

  const handleByoModalClose = useCallback(() => {
    setByoIdea(null)
    // The user may have rescheduled / added platforms inside the modal.
    const { from, to } = getViewDateRange()
    void fetchEntries(from, to)
  }, [fetchEntries])

  const handleToggleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    // A fresh popover starts clean: last attempt's error must not greet it.
    setByoError(null)
    setOpen(prev => !prev)
  }, [])

  // One shared popover+modal fragment for every variant branch, so a future
  // popover prop can't accidentally land on only some of them.
  const menuAndModal = (
    <>
      {open && (
        <CreateMomentPopover
          date={date}
          triggerRef={triggerRef}
          onScheduleOwn={handleScheduleOwn}
          byoPhase={byoPhase}
          byoError={byoError}
          onClearByoError={() => setByoError(null)}
          onClose={() => setOpen(false)}
          onCreated={onCreated}
        />
      )}
      {byoIdea && (
        <PostModal
          idea={byoIdea}
          onClose={handleByoModalClose}
          openedFrom="calendar_entry"
          entrySource="byo_upload"
        />
      )}
    </>
  )

  if (variant === 'hint') {
    return (
      <div className="relative">
        <button
          ref={triggerRef}
          onClick={handleToggleOpen}
          className={`flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] transition-all ${
            alwaysVisible
              ? 'text-paper-faint opacity-100 hover:bg-primary/5 hover:text-primary'
              : 'text-paper-faint opacity-0 group-hover/cell:opacity-100 hover:text-paper-faint'
          }`}
        >
          <Plus size={12} />
          Add post
        </button>
        {menuAndModal}
      </div>
    )
  }

  if (variant === 'full') {
    return (
      <div className="relative">
        <button
          ref={triggerRef}
          onClick={handleToggleOpen}
          className="flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-line-bright px-3 py-2 text-xs font-medium text-paper-faint transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
        >
          <Plus size={14} />
          Add post
        </button>
        {menuAndModal}
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={handleToggleOpen}
        className="flex h-5 w-5 items-center justify-center rounded-full text-paper-faint opacity-0 transition-all group-hover/cell:opacity-100 hover:bg-primary/10 hover:text-primary"
        title="Create post"
      >
        <Plus size={14} />
      </button>
      {menuAndModal}
    </div>
  )
}

const POPOVER_WIDTH = 340
/** First-paint estimate; the layout effect re-clamps with the real height. */
const POPOVER_EST_HEIGHT = 310

/**
 * The mini creation moment: the Home hero's card in miniature (Matan, round
 * 12 — one learnable grammar for both doors). Same two rooms declared up
 * front via the shared HeroModeToggle. Create with Taka: optional prompt +
 * ShapeSlider (Auto default = Taka decides) + Generate; creation goes
 * through the creation-intent endpoint and the primary creative is scheduled
 * onto the tapped day. Post your own: dropzone invite + caption + the
 * declared/locked format slider + Schedule; the parent uploads and creates
 * scheduled, then PostModal opens. Both rooms share ONE media tray (round
 * 13): as-is pixels in the own room, generation ingredients in the create
 * room, so words AND media carry across the switch. Mini means no ghost
 * prompts and no shot-hints/thinking choreography.
 */
function CreateMomentPopover({
  date,
  triggerRef,
  onScheduleOwn,
  byoPhase,
  byoError,
  onClearByoError,
  onClose,
  onCreated,
}: {
  date: string
  triggerRef: React.RefObject<HTMLButtonElement | null>
  /** Post-your-own CTA: upload + create scheduled — never credit-gated. */
  onScheduleOwn: (files: File[], caption: string) => void
  byoPhase: 'idle' | 'uploading' | 'creating'
  byoError: string | null
  /** A stale schedule error must not outlive the tray edit that addressed it. */
  onClearByoError?: () => void
  onClose: () => void
  onCreated?: () => void
}) {
  const { track } = useAnalytics()
  const { gate } = useCreditGate()
  const scheduleCreative = useCalendarStore(s => s.scheduleCreative)
  const fetchEntries = useCalendarStore(s => s.fetchEntries)
  const brandTimezone = useBrandKitStore(s => s.brandKit.timezone)
  const isMobile = useIsMobile()

  const [mode, setMode] = useState<HeroMode>('create')
  // One text state for both rooms (the hero's rule: words carry across) —
  // it's the generation prompt in one room, the caption in the other.
  const [prompt, setPrompt] = useState('')
  // null = Auto (Taka decides) — the popover uses the slider's plain variant,
  // so Auto stays bare instead of narrating an inferred pick.
  const [shape, setShape] = useState<IntentShape | null>(null)
  const [creating, setCreating] = useState(false)
  // Synchronous mirror of `creating` so a real double-click can't mint two
  // intents: the second click bails on the ref before React commits the state.
  const creatingRef = useRef(false)

  // ── Post-your-own room state ──────────────────────────────────────────────
  // Files are held locally until the CTA (unlike the pre-rooms flow, which
  // created on pick): the user sees what they dropped, can caption it, and
  // the format slider reads the media. Same declared-intent/locked-fact
  // semantics as the hero's own room.
  const [byoChoice, setByoChoice] = useState<IntentShape | null>(null)
  const [files, setFiles] = useState<{ file: File; url: string }[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const filesRef = useRef(files)
  filesRef.current = files
  useEffect(() => () => filesRef.current.forEach(f => URL.revokeObjectURL(f.url)), [])

  const busy = byoPhase !== 'idle'

  const handleModeChange = useCallback(
    (next: HeroMode) => {
      if (next === 'own' && mode !== 'own') {
        track(AnalyticsEvents.Posts.BYO_UPLOAD_OPENED, {
          source: 'calendar',
        } satisfies ByoUploadOpenedProperties)
      }
      // Room-scoped errors must not follow the user into the other room
      // (an own-room mix error reads as nonsense under the create shelf).
      setFileError(null)
      onClearByoError?.()
      setMode(next)
    },
    [mode, track, onClearByoError]
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const picked = Array.from(incoming)
      if (picked.length === 0) return
      const combined = [...filesRef.current.map(f => f.file), ...picked]
      if (mode === 'own') {
        // Judge the WHOLE would-be set so a second pick can't sneak past the
        // mix rules; on a violation keep what's on the tray and say why.
        const error = byoFilesError(combined)
        if (error) {
          setFileError(error)
          return
        }
      } else {
        // Ingredients have no mix rules (anything can inspire a generation),
        // but the hero shelf's type + size + count rules apply: the intent
        // endpoint caps at MAX_INTENT_INGREDIENTS and the media upload caps
        // per-file size, so admitting more here only defers the failure to
        // an anonymous CTA error.
        const nonMedia = picked.find(
          f => !f.type.startsWith('image/') && !f.type.startsWith('video/')
        )
        if (nonMedia) {
          setFileError('Photos and videos only (JPG, PNG, WebP, MP4, MOV).')
          return
        }
        const oversized = picked.find(f => {
          const cap = f.type.startsWith('video/')
            ? INGREDIENT_MAX_VIDEO_BYTES
            : INGREDIENT_MAX_IMAGE_BYTES
          return f.size > cap
        })
        if (oversized) {
          setFileError(
            `${oversized.name} is over ${oversized.type.startsWith('video/') ? '50MB' : '15MB'}.`
          )
          return
        }
        if (combined.length > MAX_INTENT_INGREDIENTS) {
          setFileError(`Up to ${MAX_INTENT_INGREDIENTS} photos and videos per post.`)
          return
        }
      }
      setFileError(null)
      onClearByoError?.()
      setFiles(prev => [...prev, ...picked.map(file => ({ file, url: URL.createObjectURL(file) }))])
    },
    [mode, onClearByoError]
  )

  const removeFile = useCallback(
    (url: string) => {
      URL.revokeObjectURL(url)
      setFiles(prev => prev.filter(f => f.url !== url))
      setFileError(null)
      onClearByoError?.()
    },
    [onClearByoError]
  )

  const hasClip = files.some(f => BYO_VIDEO_TYPES.includes(f.file.type))
  // As-is posts take their format FROM the media (1 video → Reel, 1 image →
  // Image, 2+ → Carousel); with files on the tray the slider shows it locked.
  const byoShape: IntentShape = hasClip
    ? 'short_vertical_video'
    : files.length >= 2
      ? 'carousel'
      : 'static_image'
  const byoInvite = byoInviteFor(byoChoice)
  // Own room: with media on the tray only images can join (one video is
  // already the whole post); before that the declared format narrows the
  // picker. Create room: ingredients stay broad, like the hero's shelf.
  const acceptAttr =
    mode === 'own'
      ? files.length > 0
        ? BYO_IMAGE_TYPES.join(',')
        : byoAcceptAttrFor(byoChoice)
      : 'image/*,video/*'
  const showAddTile =
    mode === 'own'
      ? !hasClip && files.length < BYO_MAX_CAROUSEL_ITEMS
      : files.length < MAX_INTENT_INGREDIENTS
  const canScheduleOwn = files.length > 0 && !busy

  const handleScheduleClick = useCallback(() => {
    if (!canScheduleOwn) return
    onScheduleOwn(
      filesRef.current.map(f => f.file),
      prompt.trim()
    )
  }, [canScheduleOwn, onScheduleOwn, prompt])

  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  // On tiny screens (<sm) anchoring a ~310px-tall card to a day cell almost
  // always clips no matter which way it flips; render it as a centered fixed
  // sheet over a scrim instead.
  const [centered, setCentered] = useState(false)

  const updatePos = useCallback(() => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (vw < 640) {
      setCentered(true)
      return
    }
    setCentered(false)
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const menuHeight = menuRef.current?.offsetHeight ?? POPOVER_EST_HEIGHT
    const margin = 8
    // Flip horizontally if it would overflow the right edge.
    let left = rect.left
    if (left + POPOVER_WIDTH > vw - margin) left = rect.right - POPOVER_WIDTH
    left = Math.max(margin, left)
    // Flip upward if it would overflow the bottom edge; the card is tall, so
    // when neither side fully fits, clamp it inside the viewport rather than
    // letting the CTA fall off-screen.
    let top = rect.bottom + 4
    if (top + menuHeight > vh - margin) top = rect.top - menuHeight - 4
    top = Math.min(Math.max(margin, top), Math.max(margin, vh - menuHeight - margin))
    setPos(prev => (prev && prev.top === top && prev.left === left ? prev : { top, left }))
  }, [triggerRef])

  // No dep array on purpose: the first pass positions with the height
  // estimate, and the post-render pass re-clamps with the real offsetHeight.
  // The guarded setPos makes the re-run settle instead of looping.
  useLayoutEffect(() => {
    updatePos()
  })

  useEffect(() => {
    const handle = () => updatePos()
    window.addEventListener('scroll', handle, true)
    window.addEventListener('resize', handle)
    return () => {
      window.removeEventListener('scroll', handle, true)
      window.removeEventListener('resize', handle)
    }
  }, [updatePos])

  // Close on outside click (in centered mode the scrim is outside menuRef,
  // so tapping it lands here too).
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [onClose, triggerRef])

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [onClose])

  const dayDate = new Date(date + 'T00:00:00')
  const dayLabel = format(dayDate, 'EEEE, MMM d')

  const handleCreate = useCallback(async () => {
    if (creatingRef.current) return
    // Eager credit gate: a generated post costs ≥1 credit. If the balance
    // can't cover it, pop the TopUpModal and abort before creating an intent
    // the server would only fast-fail anyway.
    if (!gate('generate_gate')) {
      onClose()
      return
    }
    creatingRef.current = true
    setCreating(true)
    const typed = prompt.trim()
    const weekdayName = format(new Date(date + 'T00:00:00'), 'EEEE')
    const fallbackPrompt = `A timely post for ${weekdayName}. Pick the strongest angle for this business right now.`
    try {
      // Tray files ride the intent as ingredients (the hero's "Show me"
      // door): dropped photos BECOME the post's imagery. Uploaded here at
      // CTA time — the mini card has no background-upload chips.
      let attachmentIds: string[] = []
      if (filesRef.current.length > 0) {
        const attachments = await Promise.all(
          filesRef.current.map(f =>
            apiCall(MediaService.mediaControllerUploadFileV1, null, { file: f.file, id: ulid() })
          )
        )
        attachmentIds = attachments.map(a => a.id)
      }
      const response = await apiCall(CreationIntentService.creationIntentControllerCreateV1, null, {
        // With ingredients and no typed idea, send no prompt at all (hero
        // parity): Taka finds the angle in the media instead of a generic
        // weekday brief talking over it.
        ...(typed ? { prompt: typed } : attachmentIds.length ? {} : { prompt: fallbackPrompt }),
        ...(attachmentIds.length ? { attachmentIds } : {}),
        ...(shape ? { shape } : {}),
        source: 'calendar',
      })

      // Generation starts server-side the moment the intent lands (no separate
      // generate-content call). Record trigger time + channel right away so
      // CalendarPage can attach duration_ms and channel to the completion
      // event when Pusher notifies us — even if scheduling below fails, the
      // generation is running and the funnel must stay whole. 'instagram' is
      // the shape's default platform in the master+variants model.
      markCalendarGenerationTriggered(response.primaryCreativeId, 'instagram')
      track(AnalyticsEvents.Calendar.CALENDAR_POST_GENERATION_TRIGGERED, {
        creative_id: response.primaryCreativeId,
        channel: 'instagram',
        shape: response.shape,
        scheduled_date: date,
      })

      // 10:00 is the business's local-wall-clock anchor (matches the
      // ConfirmPost picker default), but if the user creates onto *today*
      // after 10am we'd otherwise file the post in the past — so fall back to
      // the first slot still available that day. We resolve to the brand
      // timezone if captured, else the browser TZ — never raw UTC, which was
      // the long-standing bug that filed posts 4-12 hours off.
      const tz = brandTimezone ?? getBrowserTimezone()
      const scheduledAt = buildScheduledInstant(date, getScheduleTimeForDate(date, tz), tz)
      try {
        await scheduleCreative(response.primaryCreativeId, scheduledAt)
      } catch (scheduleErr) {
        // The intent already succeeded, so the post exists and is generating —
        // deleting it would burn the credit for nothing. Leave it as a draft
        // on Home and say so.
        console.error('Failed to schedule newly created post:', scheduleErr)
        // Keep the "it still exists as a draft" reassurance, but carry the
        // server's reason with it — otherwise the user reopens the post and
        // hits the same refusal with no idea what to change.
        toast.error(
          `Created as a draft on Home. ${extractApiError(
            scheduleErr,
            'Scheduling failed.'
          )} Open it to schedule.`
        )
        const { from, to } = getViewDateRange()
        void fetchEntries(from, to)
        onClose()
        return
      }

      const leadTimeMinutes = Math.max(
        0,
        Math.round((new Date(scheduledAt).getTime() - Date.now()) / 60_000)
      )
      track(AnalyticsEvents.Posts.POST_SCHEDULED, {
        source: 'calendar',
        creative_id: response.primaryCreativeId,
        platforms: ['instagram'],
        platform_count: 1,
        scheduled_at: scheduledAt,
        lead_time_minutes: leadTimeMinutes,
        is_immediate: false,
        entry_source: 'calendar_create',
        via_composer: false,
      })
      track(AnalyticsEvents.Calendar.CALENDAR_ENTRY_TAPPED, {
        entry_type: 'create_from_calendar',
        channel: 'instagram',
      })

      // scheduleCreative merges the entry into the store, but refetch the
      // visible range anyway (same as the BYO path) so the card carries the
      // server's full entry shape immediately.
      const { from, to } = getViewDateRange()
      void fetchEntries(from, to)
      onCreated?.()
      onClose()
    } catch (err) {
      // The intent call itself failed — nothing was created. Keep the popover
      // open so the typed idea isn't lost, and surface the server's honest
      // reason (file type/count rejects) instead of an anonymous retry loop.
      console.error('Failed to create post from calendar:', err)
      toast.error(extractApiError(err, "We couldn't create this post. Please try again."))
      creatingRef.current = false
      setCreating(false)
    }
  }, [
    gate,
    prompt,
    shape,
    date,
    brandTimezone,
    scheduleCreative,
    fetchEntries,
    track,
    onCreated,
    onClose,
  ])

  const handlePromptKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        // Enter mirrors whatever the room's CTA does.
        if (mode === 'own') {
          handleScheduleClick()
          return
        }
        void handleCreate()
      }
    },
    [mode, handleScheduleClick, handleCreate]
  )

  const filesLocked = busy || creating
  // ONE tray, two voices (the hero's shared-shelf rule): the same media rides
  // whichever room is open — as-is pixels in Post your own, ingredients in
  // Create with Taka — so switching rooms never loses a drop.
  const mediaTray = (
    <div
      className="mt-2 flex flex-wrap gap-2 rounded-[9px] border border-dashed border-line px-2.5 py-2.5"
      // The tray replaced the dropzone that TAUGHT dropping — it must keep
      // catching drops (an unprevented file drop navigates the tab to the
      // file and destroys the popover). Prevent ALWAYS, gate only the action.
      onDragOver={e => e.preventDefault()}
      onDrop={e => {
        e.preventDefault()
        if (filesLocked) return
        addFiles(e.dataTransfer.files)
      }}
    >
      {files.map(f => (
        <span key={f.url} className="relative">
          {f.file.type.startsWith('video/') ? (
            <video
              src={f.url}
              muted
              playsInline
              preload="metadata"
              className="aspect-[4/5] w-14 rounded-[8px] border border-line object-cover"
            />
          ) : (
            <img
              src={f.url}
              alt={f.file.name}
              className="aspect-[4/5] w-14 rounded-[8px] border border-line object-cover"
            />
          )}
          {!filesLocked && (
            <button
              type="button"
              onClick={() => removeFile(f.url)}
              aria-label={`Remove ${f.file.name}`}
              className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-bg-2 text-paper-faint transition-colors hover:text-paper"
            >
              <X size={10} />
            </button>
          )}
        </span>
      ))}
      {showAddTile && (
        <button
          type="button"
          disabled={filesLocked}
          onClick={() => fileInputRef.current?.click()}
          aria-label={mode === 'own' ? 'Add more photos' : 'Add more photos or clips'}
          className="flex aspect-[4/5] w-14 items-center justify-center rounded-[8px] border border-dashed border-line text-paper-faint transition-colors hover:border-acid/50 hover:text-acid"
        >
          <Plus size={12} />
        </button>
      )}
    </div>
  )

  if (!centered && !pos) return null

  return createPortal(
    <>
      {centered && <div aria-hidden="true" className="fixed inset-0 z-[999] bg-black/50" />}
      <div
        ref={menuRef}
        role="dialog"
        aria-label={`Add a post on ${dayLabel}`}
        style={
          centered
            ? {
                position: 'fixed',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: `min(${POPOVER_WIDTH}px, calc(100vw - 24px))`,
                zIndex: 1000,
              }
            : {
                position: 'fixed',
                top: pos!.top,
                left: pos!.left,
                width: POPOVER_WIDTH,
                zIndex: 1000,
              }
        }
        className="overflow-hidden rounded-[12px] border bg-bg-2 shadow-lg"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="px-4 pt-3.5 pb-4">
          <p className="text-[12px] text-paper-dim">{dayLabel}</p>
          {/* The rooms, declared up front — the hero's grammar in miniature. */}
          <div className="mt-2 flex justify-end">
            <HeroModeToggle
              value={mode}
              disabled={creating || busy}
              isMobile={isMobile}
              onChange={handleModeChange}
            />
          </div>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={handlePromptKeyDown}
            rows={mode === 'own' ? 2 : 3}
            autoFocus={!isMobile}
            disabled={creating || busy}
            placeholder={mode === 'own' ? 'Write your caption' : 'What should this post be about?'}
            aria-label={mode === 'own' ? 'Write your caption' : 'What should this post be about?'}
            className="mt-2 w-full resize-none rounded-[9px] border border-line bg-bg px-3 py-2 text-[13px] leading-snug text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid/60 disabled:opacity-60"
          />
          {mode === 'own' ? (
            <>
              {files.length === 0 ? (
                /* THE POST-YOUR-OWN INVITE, compact: a real dropzone, same
                   voice as the hero's, retold by the declared format. */
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => {
                    e.preventDefault()
                    setDragOver(false)
                    if (e.dataTransfer.files) addFiles(e.dataTransfer.files)
                  }}
                  aria-label="Add your finished video or photos. Taka publishes them exactly as uploaded."
                  className={cn(
                    'group/invite mt-2 flex w-full cursor-pointer flex-col gap-1.5 rounded-[9px] border border-dashed px-3 py-2.5 text-left transition-colors',
                    dragOver
                      ? 'border-acid bg-acid/10'
                      : 'border-line hover:border-acid/50 hover:bg-bg-3/60',
                    busy && 'pointer-events-none opacity-60'
                  )}
                >
                  <span className="flex w-full items-center gap-1.5" aria-hidden="true">
                    <Upload size={13} className="flex-shrink-0 text-acid" />
                    <span className="text-[12.5px] font-medium text-paper">
                      {dragOver ? 'Drop it in.' : byoInvite.title}
                    </span>
                  </span>
                  <span
                    className="block text-[11.5px] leading-snug text-paper-faint"
                    aria-hidden="true"
                  >
                    {byoInvite.sub}
                  </span>
                  <span className="mt-0.5 flex gap-2" aria-hidden="true">
                    {Array.from({ length: byoInvite.frames }, (_, i) => (
                      <span
                        key={i}
                        className="flex aspect-[4/5] w-12 flex-col items-center justify-center rounded-[8px] border border-dashed border-line transition-colors group-hover/invite:border-acid/50"
                      >
                        {byoChoice === 'short_vertical_video' ? (
                          <Video
                            size={12}
                            className="text-paper-faint transition-colors group-hover/invite:text-acid"
                          />
                        ) : (
                          <Plus
                            size={12}
                            className="text-paper-faint transition-colors group-hover/invite:text-acid"
                          />
                        )}
                      </span>
                    ))}
                  </span>
                </button>
              ) : (
                /* The mini tray: what's dropped is what posts, in this order. */
                mediaTray
              )}
              {/* Same semantics as the hero's own room: a real choice before
                  files (declared intent), the media's locked fact after. */}
              <div className="mt-2.5 flex">
                <ShapeSlider
                  hideAuto
                  value={byoChoice}
                  lockedShape={files.length > 0 ? byoShape : null}
                  isMobile={isMobile}
                  disabled={busy}
                  onChange={next => setByoChoice(cur => (next === cur ? null : next))}
                />
              </div>
              {(fileError ?? byoError) && (
                <p className="mt-1.5 text-[12px] leading-snug text-[color:var(--alert-error-text)]">
                  {fileError ?? byoError}
                </p>
              )}
              <Button
                variant="t3-primary"
                size="t3-sm"
                className="mt-3 w-full"
                disabled={!canScheduleOwn}
                onClick={handleScheduleClick}
              >
                {byoPhase === 'uploading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Uploading…
                  </>
                ) : byoPhase === 'creating' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Adding to the calendar…
                  </>
                ) : (
                  'Schedule my post'
                )}
              </Button>
            </>
          ) : (
            <>
              {files.length > 0 ? (
                /* Ingredients: the same tray, now feeding the generation. */
                mediaTray
              ) : (
                /* The create room's "Show me" door, one quiet line (the
                   hero's idle-shelf pitch in miniature). */
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => {
                    e.preventDefault()
                    setDragOver(false)
                    if (e.dataTransfer.files) addFiles(e.dataTransfer.files)
                  }}
                  className={cn(
                    'mt-2 flex w-full items-center gap-1.5 rounded-[9px] border border-dashed px-3 py-2 text-left text-[12px] transition-colors',
                    dragOver
                      ? 'border-acid bg-acid/10 text-paper'
                      : 'border-line text-paper-faint hover:border-acid/50 hover:text-paper',
                    creating && 'pointer-events-none opacity-60'
                  )}
                >
                  <Plus size={12} className="flex-shrink-0" />
                  {dragOver
                    ? 'Drop them in. These become the post.'
                    : 'Add photos or clips to make this post feel real.'}
                </button>
              )}
              {fileError && (
                <p className="mt-1.5 text-[12px] leading-snug text-[color:var(--alert-error-text)]">
                  {fileError}
                </p>
              )}
              {/* The acid thumb is the popover's one bit of personality; Auto
                  is the default and means Taka decides (shape omitted from
                  the call). */}
              <div className="mt-2.5 flex">
                <ShapeSlider value={shape} onChange={setShape} disabled={creating} />
              </div>
              <Button
                variant="t3-primary"
                size="t3-sm"
                className="mt-3 w-full"
                disabled={creating}
                onClick={() => void handleCreate()}
              >
                {creating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Starting…
                  </>
                ) : (
                  'Generate my post'
                )}
              </Button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files) addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </div>
    </>,
    document.body
  )
}
